import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Bookmark, Bot, ChartColumn, Cpu, Folder, LogOut, MessageCircle, Play, Plus, Search, Server, Shield } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { fuzzyMatch, type ConversationDTO } from '@harness/shared';
import { api } from '../lib/api';
import { qk } from '../lib/keys';
import { useProjects } from '../lib/queries';
import { ui, useUI } from '../lib/store';
import { statusLabel } from './ui';

interface Item {
  group: 'Projects' | 'Conversations' | 'Commands';
  label: string;
  sub?: string;
  icon: ReactNode;
  run: () => void;
}

const I = (C: typeof Search) => <C className="icon" />;

export default function CommandPalette() {
  const open = useUI((s) => s.palette);
  if (!open) return null;
  return <Palette />;
}

function Palette() {
  const nav = useNavigate();
  const loc = useLocation();
  const qc = useQueryClient();
  const { data: projects = [] } = useProjects();
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const close = () => ui.set({ palette: false });

  useEffect(() => input.current?.focus(), []);

  const slug = loc.pathname.match(/^\/p\/([^/]+)/)?.[1];
  const current = projects.find((p) => p.slug === slug) ?? projects.find((p) => !p.archived);

  // Server-side search through message text (titles are matched locally).
  const search = useQuery({
    queryKey: ['search', q],
    queryFn: () => api.get<{ conversationId: string; title: string; projectSlug: string; projectName: string; excerpt: string }[]>(`/api/search?q=${encodeURIComponent(q)}`),
    enabled: q.trim().length >= 3,
    staleTime: 30_000,
  });

  const items = useMemo(() => {
    const go = (path: string) => () => nav(path);
    const pname = current?.name ?? 'project';
    const cmds = ([
      { group: 'Commands', label: 'New conversation', sub: current ? `in ${pname}` : '', icon: I(Plus), run: () => current && (nav(`/p/${current.slug}`), setTimeout(() => window.dispatchEvent(new Event('harness:new-conversation')), 50)) },
      { group: 'Commands', label: 'Start feature from Jira task', sub: '/feature', icon: I(Play), run: () => current && (nav(`/p/${current.slug}`), setTimeout(() => window.dispatchEvent(new CustomEvent('harness:ctx-tab', { detail: 'tasks' })), 50)) },
      { group: 'Commands', label: 'Switch model', sub: 'for the next turn', icon: I(Cpu), run: () => current && (nav(loc.pathname.startsWith(`/p/${current.slug}/c/`) ? loc.pathname : `/p/${current.slug}`), setTimeout(() => window.dispatchEvent(new Event('harness:model-menu')), 80)) },
      { group: 'Commands', label: 'Agent settings', sub: pname, icon: I(Bot), run: go(`/p/${current?.slug}/settings/agents`) },
      { group: 'Commands', label: 'Project memory', sub: pname, icon: I(Bookmark), run: go(`/p/${current?.slug}/settings/memory`) },
      { group: 'Commands', label: 'Server connection', sub: pname, icon: I(Server), run: go(`/p/${current?.slug}/settings/server`) },
      { group: 'Commands', label: 'Providers & models', icon: I(Cpu), run: go('/settings/providers') },
      { group: 'Commands', label: 'New project', icon: I(Folder), run: go('/new') },
      { group: 'Commands', label: 'All projects', icon: I(Folder), run: go('/') },
      { group: 'Commands', label: 'Usage', sub: 'tokens and cost', icon: I(ChartColumn), run: go('/settings/usage') },
      { group: 'Commands', label: 'Account & security', sub: '2FA, passkeys, sessions', icon: I(Shield), run: go('/settings/account') },
      {
        group: 'Commands',
        label: 'Sign out',
        icon: I(LogOut),
        run: async () => {
          await api.post('/api/auth/logout');
          qc.clear();
          await qc.invalidateQueries({ queryKey: qk.session });
          nav('/login');
        },
      },
    ] as Item[]).filter((c) => current || !c.sub?.startsWith('in '));

    const projs: Item[] = projects
      .filter((p) => !p.archived)
      .map((p) => ({ group: 'Projects', label: p.name, sub: `${p.server ? statusLabel(p.server.status) : 'No server'} · ${p.defaultModel ?? 'no model'}`, icon: I(Folder), run: go(`/p/${p.slug}`) }));

    const convs: Item[] = [];
    for (const [key, list] of qc.getQueriesData<ConversationDTO[]>({ queryKey: ['conversations'] })) {
      const p = projects.find((x) => x.id === key[1]);
      if (!p || !list) continue;
      for (const c of list) convs.push({ group: 'Conversations', label: c.title, sub: p.name, icon: I(MessageCircle), run: go(`/p/${p.slug}/c/${c.id}`) });
    }
    for (const r of search.data ?? []) {
      if (!convs.some((c) => c.label === r.title && c.sub === r.projectName))
        convs.push({ group: 'Conversations', label: r.title, sub: `${r.projectName} · “${r.excerpt.trim().slice(0, 60)}”`, icon: I(MessageCircle), run: go(`/p/${r.projectSlug}/c/${r.conversationId}`) });
    }

    const query = q.trim();
    // Rank: label prefix > label substring > label subsequence > subtitle substring. Groups keep their order.
    const score = (x: Item) => {
      const l = x.label.toLowerCase();
      const qq = query.toLowerCase();
      if (l.startsWith(qq)) return 4;
      if (l.includes(qq)) return 3;
      if (fuzzyMatch(qq, l)) return 2;
      if ((x.sub ?? '').toLowerCase().includes(qq)) return 1;
      if (x.group === 'Conversations' && (search.data ?? []).some((r) => r.title === x.label)) return 1;
      return 0;
    };
    const rank = (list: Item[]) =>
      list
        .map((x) => [x, score(x)] as const)
        .filter(([, sc]) => sc > 0)
        .sort((a, b) => b[1] - a[1])
        .map(([x]) => x);
    const out = query ? [...rank(projs), ...rank(convs), ...rank(cmds)] : [...projs.slice(0, 4), ...convs.slice(0, 3), ...cmds.slice(0, 5)];
    return out.slice(0, 12);
  }, [q, projects, current, search.data, qc, nav, loc.pathname]);

  const idx = Math.min(sel, Math.max(0, items.length - 1));
  const runAt = (i: number) => {
    const it = items[i];
    if (!it) return;
    close();
    it.run();
  };

  useEffect(() => {
    listRef.current?.querySelector('[data-sel="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [idx]);

  let lastGroup = '';
  return (
    <div className="palette-backdrop" onMouseDown={close}>
      <div className="palette" role="dialog" aria-label="Command palette" onMouseDown={(e) => e.stopPropagation()}>
        <div className="palette-input">
          <Search className="icon" style={{ fontSize: 18 }} />
          <input
            ref={input}
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setSel(0);
            }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setSel(Math.min(idx + 1, items.length - 1));
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setSel(Math.max(idx - 1, 0));
              } else if (e.key === 'Enter') {
                e.preventDefault();
                runAt(idx);
              } else if (e.key === 'Escape') {
                e.preventDefault();
                close();
              }
            }}
            placeholder="Search projects, conversations and commands"
            aria-label="Search"
            role="combobox"
            aria-expanded
            aria-controls="palette-list"
          />
          <span className="kbd">esc</span>
        </div>
        <div className="palette-list" id="palette-list" role="listbox" ref={listRef}>
          {items.map((it, i) => {
            const header = it.group !== lastGroup ? it.group : '';
            lastGroup = it.group;
            return (
              <div key={`${it.group}-${it.label}-${it.sub}-${i}`}>
                {header && <div className="micro palette-group">{header}</div>}
                <button role="option" aria-selected={i === idx} data-sel={i === idx} className="palette-item" onMouseEnter={() => setSel(i)} onClick={() => runAt(i)}>
                  <span style={{ fontSize: 15 }}>{it.icon}</span>
                  <span className="ellipsis" style={{ flex: 1 }}>
                    <span className="l">{it.label}</span> <span style={{ fontSize: 12, opacity: 0.75 }}>{it.sub}</span>
                  </span>
                  {i === idx && <span style={{ fontSize: 12 }}>↵</span>}
                </button>
              </div>
            );
          })}
          {items.length === 0 && <div className="empty">Nothing matches “{q}”.</div>}
        </div>
        <div className="palette-foot">
          <span>↑↓ to move</span>
          <span>↵ to open</span>
          <span>⌘K to toggle</span>
        </div>
      </div>
    </div>
  );
}
